import { ref } from 'vue'
import {
  ArticleListRequestParam,
  ContentKind,
  ContentStatus,
  ItemsResponse,
  ListResponse,
  TagGroup,
  TagID,
} from '/@src/models/api'
import { FindArticleListDocument, getArticles } from '/@src/api/contentRepo'
import { ArticleModel, RawArticle } from '/@src/models/content'
import useApi, { useApollo } from '/@src/composable/useApi'
import {
  Article,
  Content,
  Exact,
  Maybe,
  Scalars,
  Tag,
  TagRelation,
} from '/@src/models/graphApi'
import {
  ContentsQueryParams,
  FindContentsQueryVariables,
} from '/@src/composable/content/useContents'
import useTagRelations from '/@src/composable/content/useTagRelations'

export type FindArticleListQuery = {
  __typename?: 'query'
  articles?:
    | {
        __typename?: 'ArticlePage'
        total_count?: number | null | undefined
        items?:
          | Array<
              | {
                  __typename?: 'Article'
                  id?: number | null | undefined
                  title?: string | null | undefined
                  summary?: string | null | undefined
                  cover_url?: string | null | undefined
                  is_cooperation?: boolean | null | undefined
                  planned_published_at?: any | null | undefined
                  display_time?: any | null | undefined
                  vip_visibility?: number | null | undefined
                  words_count?: number | null | undefined
                  status?: string | null | undefined
                  counter?:
                    | {
                        __typename?: 'Counter'
                        viewed?: number | null | undefined
                        commented?: number | null | undefined
                      }
                    | null
                    | undefined
                  tags?:
                    | Array<
                        | {
                            __typename?: 'Tag'
                            id?: number | null | undefined
                            name?: string | null | undefined
                            cover_url?: string | null | undefined
                            is_topic?: boolean | null | undefined
                          }
                        | null
                        | undefined
                      >
                    | null
                    | undefined
                  tag_relations?:
                    | Array<
                        | {
                            __typename?: 'TagRelation'
                            id?: number | null | undefined
                            group?: number | null | undefined
                          }
                        | null
                        | undefined
                      >
                    | null
                    | undefined
                  author?:
                    | {
                        __typename?: 'Author'
                        id?: number | null | undefined
                        nickname?: string | null | undefined
                        avatar?: string | null | undefined
                      }
                    | null
                    | undefined
                }
              | null
              | undefined
            >
          | null
          | undefined
      }
    | null
    | undefined
}
export type FindArticleListQueryVariables = Exact<{
  track?: Maybe<Scalars['String']>
  first?: Maybe<Scalars['Int']>
  offset?: Maybe<Scalars['Int']>
  status?: Maybe<Scalars['Int']>
  user_id?: Maybe<Scalars['Int']>
  tag_id?: Maybe<Scalars['Int']>
  group?: Maybe<Scalars['Int']>
  vip_visibility?: Maybe<Scalars['Int']>
  wc_lt?: Maybe<Scalars['Int']>
  wc_gt?: Maybe<Scalars['Int']>
}>
export default function useArticles() {
  const api = useApi()
  const graph = useApollo()
  const { updateTagsWithRelations } = useTagRelations()
  const articleResponse = ref<ListResponse<RawArticle>>()
  const articleListResponse = ref<ItemsResponse<ArticleModel>>()
  articleListResponse.value = {
    items: [],
    total_count: 0,
  }
  const getArticleList = async (param: ArticleListRequestParam) => {
    if (param.limit === 0) {
      param.limit = 10
    }
    const ret = await getArticles(api, param)
    ret.list.map((it) => {
      it.kind = ContentKind.ARTICLE
    })
    articleResponse.value = ret
  }
  /**
   * 将Contents数据类型转换至文章
   * @param contents
   * @param total 文章总数
   * @param page 当前是第几页的数据
   */
  const mapperContentsToArticleList = (
    contents: Array<Content>,
    total: number,
    page: number
  ) => {
    const articles: Array<ArticleModel> = []
    contents.map((it) => {
      let isHome = false
      let scheduleRelease = undefined
      const article = it.detail as Article
      if (it.detail) {
        if (
          article.planned_published_at &&
          new Date(article.planned_published_at) > new Date()
        ) {
          scheduleRelease = article.planned_published_at
        }
        if (article.tag_relations && article.tag_relations.length > 0) {
          for (const relation of article.tag_relations) {
            if (
              relation!.id === TagID.MATRIX &&
              relation!.group === TagGroup.FEATURED_FLOW
            ) {
              isHome = true
              break
            }
          }
        }
      }

      articles.push({
        id: it.raw_id!,
        author: it.author,
        cover_url: it.cover_url,
        status: it.status.toLowerCase(),
        is_cooperation: article!.is_cooperation,
        display_time: it!!.datetime,
        planned_published_at: scheduleRelease,
        isHome: isHome,
        words_count: article.words_count,
        counter: it.counter,
        comment_count: it.counter!.commented!,
        page_views: it.counter?.viewed!,
        title: it.title!,
        tags: it.tags,
        vip_visibility: it.vip_visibility,
        tag_relations: article.tag_relations,
      })
    })
    articleListResponse.value = {
      items: articles,
      total_count: total,
      page_info: {
        page: page,
      },
    }
  }
  const mapperToRawArticle = (contents: Content[], total: number) => {
    if (
      articleListResponse.value?.items &&
      articleListResponse.value.items.length > 0
    ) {
      articleResponse.value = {
        list: [],
        count: articleListResponse.value?.total_count,
      }
    }
    articleResponse.value = {
      list: [],
      count: total,
    }
    contents.forEach((it) => {
      // @ts-ignore
      articleResponse.value.list.push({
        id: it.raw_id!,
        kind: ContentKind.ARTICLE,
        title: it.title!,
        status: it.status as ContentStatus,
        display_time: it.datetime,
        cover_uri: it.cover_url,
        vip_visibility: it.vip_visibility!,
        author: (it.detail as Article).author,
      })
    })
  }
  /**
   * graphql调用新文章列表
   * @param params
   */
  const queryArticleList = async (viewParams: ContentsQueryParams) => {
    const params: FindArticleListQueryVariables = {
      first: viewParams.first,
      offset: viewParams.offset,
      group: viewParams.group,
      track: viewParams.track,
      vip_visibility: viewParams.contentLevel,
      status: viewParams.status,
      tag_id: viewParams.tagId,
      user_id: viewParams.userId,
      wc_lt: viewParams.wordsLessThan,
      wc_gt: viewParams.wordsGreaterThan,
    }
    const res = await graph.query<
      FindArticleListQuery,
      FindArticleListQueryVariables
    >({ query: FindArticleListDocument, variables: params, errorPolicy: 'all' })
    if (res.data) {
      const newData: ArticleModel[] = []
      res.data.articles!!.items!!.map((it) => {
        let scheduleRelease = undefined
        if (
          it!!.planned_published_at &&
          new Date(it!!.planned_published_at) > new Date()
        ) {
          scheduleRelease = it!!.planned_published_at
        }
        let isHome = false
        if (it!.tag_relations && it!.tag_relations.length > 0) {
          for (const relation of it!.tag_relations) {
            if (
              relation!.id === TagID.MATRIX &&
              relation!.group === TagGroup.FEATURED_FLOW
            ) {
              isHome = true
              break
            }
          }
        }
        let tags = it!.tags
        if (it!.tag_relations) {
          tags = updateTagsWithRelations(
            it!.tag_relations as TagRelation[],
            it!.tags as Tag[]
          )
        }
        newData.push({
          id: it!.id,
          isHome: isHome,
          is_cooperation: it!.is_cooperation,
          display_time: it!.display_time,
          planned_publish_at: scheduleRelease,
          planned_published_at: scheduleRelease,
          title: it!.title,
          summary: it!.summary,
          tags: tags,
          tag_relations: it!.tag_relations,
          cover_url: it!.cover_url,
          counter: it!.counter,
          words_count: it!.words_count,
          vip_visibility: it!.vip_visibility,
          author: it!.author,
          status: it!.status?.toLowerCase(),
        })
      })

      articleListResponse.value = {
        items: newData,
        total_count: res.data.articles?.total_count!!,
      }
    }
  }
  return {
    articleResponse,
    getArticleList,
    articleListResponse,
    mapperContentsToArticleList,
    queryArticleList,
    mapperToRawArticle,
  }
}
